import React from "react";
import { VerifyAgeContext, VerifyStyle } from "./VerifyAgeContext";
import { GrownupsHurdleBody } from "../../components/GrownupsHurdleBody";
import Modal from "react-bootstrap/Modal";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { Trans } from "react-i18next";
import { AppFlowActionButton } from "../CommonStyles";

interface Props {
  children: React.ReactNode;
}

const ContinueButton = styled(AppFlowActionButton)`
  font-size: 24px;
`;

export const VerifyAgeProvider = (props: Props) => {
  const { t } = useTranslation();

  const [lastCallback, setLastCallback] = React.useState<
    ((success: boolean) => void) | null
  >(null);
  const [verifyStyle, setVerifyStyle] = React.useState<VerifyStyle | null>(
    null
  );

  const verifyAge = React.useCallback(
    (
      callback: (success: boolean) => void,
      style: VerifyStyle = VerifyStyle.HURDLE
    ) => {
      setLastCallback(() => callback);
      setVerifyStyle(style);
    },
    []
  );

  const contextValue = React.useMemo(() => ({ verifyAge }), [verifyAge]);

  const [canContinue, setCanContinue] = React.useState(false);
  const [month, setMonth] = React.useState("");
  const [day, setDay] = React.useState("");
  const [year, setYear] = React.useState("");

  const reset = () => {
    setCanContinue(false);
    setMonth("");
    setDay("");
    setYear("");
  };

  React.useEffect(() => {
    if (
      month === "" ||
      day === "" ||
      year === "" ||
      +year < 1910 ||
      +day < 1 ||
      +day > 31
    ) {
      setCanContinue(false);
      return;
    }
    const birthDate = new Date(+year, +month, +day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    let m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    setCanContinue(age >= 18);
  }, [month, day, year]);

  return (
    <>
      <VerifyAgeContext.Provider value={contextValue}>
        {props.children}
      </VerifyAgeContext.Provider>

      <Modal
        show={lastCallback != null}
        onHide={() => {
          const previousCallback = lastCallback;
          setLastCallback(null);
          previousCallback!(false);
          reset();
        }}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {verifyStyle === VerifyStyle.DATA_HANDLING
              ? t("verify_age_title")
              : t("grownups_only")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {verifyStyle === VerifyStyle.DATA_HANDLING && (
            <div className="text-center my-5">
              <Trans t={t} i18nKey="verify_age_explanation">
                In order to agree to the Super Teacher{" "}
                <a
                  href="/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="/terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                , you must be over the age of 18.
              </Trans>
            </div>
          )}

          <div className="text-center my-3">
            <Trans t={t} i18nKey="verify_age_explanation2">
              Please verify your <strong>date of birth</strong> to continue.
            </Trans>
          </div>

          <div className="text-center">
            <GrownupsHurdleBody
              day={day}
              setDay={setDay}
              month={month}
              setMonth={setMonth}
              year={year}
              setYear={setYear}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ContinueButton
            disabled={!canContinue}
            isEnabled={canContinue}
            onClick={() => {
              if (canContinue) {
                const previousCallback = lastCallback;
                setLastCallback(null);
                previousCallback!(true);
                reset();
              }
            }}
          >
            {t("verify_age_continue")}
          </ContinueButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
